import { useEffect, useRef, useState } from 'react';
import { BrandFilter } from '@apps/components/BrandFilter';
import { CarLaunchMonthHeaderNavBarItem } from '@apps/components/CarLaunch/CarLaunchMonthNavBarItem';
import { CarLaunchMonthNavItemRefProps } from '@apps/components/CarLaunch/CarLaunchMonthNavBarItem/props';
import { CarLaunchMonthNavBarItem } from '@apps/components/CarLaunch/CarLaunchMonthNavBarItem/settings';
import { CarLaunchMonthSectionContent } from '@apps/components/CarLaunch/CarLaunchMonthSectionContent';
import { createNewLaunchSectionData } from '@apps/components/CarLaunch/CarLaunchMonthSectionContent/helper';
import { CarLaunchMonthSectionType } from '@apps/components/CarLaunch/CarLaunchMonthSectionContent/props';
import { CarLaunchNews } from '@apps/components/CarLaunch/CarLaunchNews';
import { DialogRefProps } from '@apps/components/Dialog';
import Popover from '@apps/components/Popover';
import QuickFilterButton from '@apps/components/QuickFilterButton';
import { getNewCarByFilter } from '@apps/config/API/new-car.config';
import FilterSection from '@apps/containers/FilterSection';
import RecommendCarList from '@apps/containers/RecommendCarList';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import { CategoriesBoxWrapper } from '@apps/layouts/Navbar/styles';
import {
  generateQueryToUrl,
  generateUrlToQuery,
} from '@apps/utils/query-to-url';
import { Avatar, Box, Button, Divider, Stack, Typography } from '@mui/material';
import FilterIcon from '@stack/assets/icons/filter.svg';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { Breadcrumb, TitleDivider } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { redirect } from '@webbyx/next-js';
import _, { isEmpty, omit, size } from 'lodash';
import { useRouter } from 'next/router';

import {
  CapacityType,
  FuelType,
  SeatType,
  SegmentType,
  TransmissionType,
} from '../NewCar';

import { useQuickButtonGroup } from './settings';

/**
 * ===========================
 * MAIN
 * ===========================
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const NewLaunchScreen = (props: {
  carNewLaunch: CarLaunchMonthSectionType[];
  yearMonthObject: any;
  popularBrand: any;
  filterList: CarLaunchMonthSectionType[];
  settings?: {
    budget: number[];
    monthly_payment: number[];
    segment: SegmentType[];
    capacity: CapacityType[];
    fuel_type: FuelType[];
    transmission: TransmissionType[];
    seat: SeatType[];
  };
  webView?: boolean;
}) => {
  // =============== VARIABLES
  const {
    carNewLaunch,
    yearMonthObject,
    popularBrand,
    filterList,
    settings,
    webView,
  } = props;

  const { clickAction } = usePostMsgContext();
  const { basePath } = createBasePath({ webView, type: 'cars' });
  const { isMobile, lg, sm } = useGetResponsive();

  const sectionRef = useRef<
    Record<CarLaunchMonthNavBarItem, HTMLElement | null>
  >({} as Record<CarLaunchMonthNavBarItem, HTMLElement | null>);
  const navItemRef = useRef<CarLaunchMonthNavItemRefProps>(null);

  const desktopPopularBrands = popularBrand?.slice(0, 16) ?? [];

  // =============== HOOKS
  const router = useRouter();

  const query = router?.query;
  const budget = settings?.budget?.slice(0, -1);
  const monthlyPayment = settings?.monthly_payment?.slice(0, -1);
  const minBudget = Math.min(...(budget ?? []));
  const maxBudget = Math.max(...(budget ?? []));
  const minMonthly = Math.min(...(monthlyPayment ?? []));
  const maxMonthly = Math.max(...(monthlyPayment ?? []));
  const defaultBudget =
    query['min_budget'] || query['max_budget']
      ? {
          budget: [
            query['min_budget'] ?? minBudget,
            query['max_budget'] ?? maxBudget,
          ],
        }
      : {};
  const defaultMonthly =
    query['min_monthly_payment'] || query['max_monthly_payment']
      ? {
          monthly_payment: [
            query['min_monthly_payment'] ?? minBudget,
            query['max_monthly_payment'] ?? maxBudget,
          ],
        }
      : {};
  const defaultFilter = query
    ? generateUrlToQuery(query as any, [
        'min_budget',
        'max_budget',
        'min_monthly_payment',
        'max_monthly_payment',
        'page',
      ])
    : null;
  const [filterData, setFilterData] =
    useState<CarLaunchMonthSectionType[]>(filterList);
  const formatter = new Intl.NumberFormat('en-US');
  const [popperItem, setPopperItem] = useState<typeof quickButtons[0] | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterValue, setFilterValue] = useState<any>({
    ...defaultFilter,
    ...defaultBudget,
    ...defaultMonthly,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const filterDialogRef = useRef<DialogRefProps>(null);

  const quickButtons = useQuickButtonGroup({
    budgetList: budget,
    monthlyPaymentList: monthlyPayment,
  });

  const isFiltered = Object.values(filterValue ?? {}).some(
    (value) => !isEmpty(value)
  );

  const totalFilter = Object.entries(filterValue ?? {})?.reduce(
    (acc, [key, value]: any) => {
      const keysIgnore = ['page'];
      // const rangesKey = ['monthly_payment', 'budget'];

      if (keysIgnore.includes(key)) return acc;
      // if (rangesKey.includes(key) && !isEmpty(value)) return (acc += 1);
      if (!isEmpty(value)) return (acc += 1);

      return acc;
    },
    0
  );

  useEffect(() => {
    const { page } = router.query;
    if (page) {
      onFilter({ ...defaultFilter, ...defaultBudget, ...defaultMonthly, page });
    }
  }, [router?.asPath, router?.locale]);

  useEffect(() => {
    if (popperItem) return () => null;
    const handleScroll = () => {
      const sections = document.querySelectorAll(
        '[id^="carLaunchMonthSection_"]'
      );
      const headerOffset =
        document.getElementById('carLaunchFilterHeader')?.offsetHeight ?? 0;
      let newCurrentItem = null;

      sections.forEach((section) => {
        const sectionId = section.id.replace('carLaunchMonthSection_', '');
        const rect = section.getBoundingClientRect();

        if (rect.top < 40 + headerOffset) {
          newCurrentItem = sectionId as CarLaunchMonthNavBarItem;
        }
      });
      if (
        newCurrentItem &&
        newCurrentItem !== navItemRef.current?.focusedNavItem
      ) {
        navItemRef.current?.setFocusedNavItem(newCurrentItem);

        // Move the nav item to the center of the nav container
        const targetNavItemElement = document.getElementById(
          `carLaunchMonthNavItem_${newCurrentItem}`
        );
        const navContainerElement = document.getElementById(
          'carLaunchFilterHeader'
        );

        if (targetNavItemElement && navContainerElement) {
          const scrollLeft =
            targetNavItemElement.offsetLeft -
            navContainerElement.clientWidth / 2 +
            targetNavItemElement.clientWidth / 2;

          navItemRef.current?.element?.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navItemRef]);

  // =============== EVENTS
  const onHandleClose = () => {
    setPopperItem(null);
    setAnchorEl(null);
  };

  const onHandleClick = (
    selectedItem: typeof quickButtons[0],
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (!selectedItem?.render) {
      onFilter(selectedItem?.filterQuery);
      return;
    }
    setAnchorEl(event?.currentTarget);
    if (popperItem?.key === selectedItem?.key) {
      onHandleClose();
      return;
    }
    setPopperItem(selectedItem);
  };

  const onReset = () => {
    router.replace(
      {
        query: {},
      },
      undefined,
      { shallow: true }
    );
    setFilterValue(null);
    setFilterData([]);
    onClose();
  };

  const onFilter = async (query: any) => {
    setFilterValue(query);
    const pathQuery = checkDefault(query) as any;
    let newFilterData: CarLaunchMonthSectionType[] = [];
    const filterUrl = generateQueryToUrl(pathQuery, `${basePath}/new-launches`);
    redirect({}, filterUrl, 'replace');
    if (!_.isEmpty(pathQuery)) {
      const { data: filterNewLaunchCars } = await getNewCarByFilter({
        query: pathQuery,
        path: `car_info/new_launch`,
      });
      newFilterData = createNewLaunchSectionData({
        yearMonthObject: yearMonthObject,
        newLaunchCars: filterNewLaunchCars,
      });
    }
    setFilterData(newFilterData);
    onClose();
  };

  const filterRangeInput = (
    minKey: string,
    maxKey: string,
    minValue: number,
    maxValue: number,
    value?: string | string[]
  ) => {
    if (!value) return {};
    const [min, max] = value;
    let routerQuery: {
      [key: string]: string | undefined | string[];
    } = {
      [minKey]: min,
      [maxKey]: max,
    };
    if (Number(min) === minValue) {
      routerQuery = omit(routerQuery, [minKey]);
    }
    if (Number(max) === maxValue) {
      routerQuery = omit(routerQuery, [maxKey]);
    }
    return routerQuery;
  };

  const checkDefault = (value: any) => {
    const segment_type = value?.['segment'];
    const body_type = value?.['body_type'];
    const budget = value?.['budget'];
    const monthlyPayment = value?.['monthly_payment'];
    const budgetValue = filterRangeInput(
      'min_budget',
      'max_budget',
      minBudget,
      maxBudget,
      budget
    );
    const monthlyPaymentValue = filterRangeInput(
      'min_monthly_payment',
      'max_monthly_payment',
      minMonthly,
      maxMonthly,
      monthlyPayment
    );
    return {
      ...(budget && budgetValue),
      ...(monthlyPayment && monthlyPaymentValue),
      ...(body_type && !isEmpty(body_type) && { body_type: body_type }),
      ...(value?.['min_budget'] && { min_budget: value['min_budget'] }),
      ...(value?.['max_budget'] && { min_budget: value['max_budget'] }),
      ...(value?.['min_monthly_payment'] && {
        min_budget: value['min_monthly_payment'],
      }),
      ...(value?.['max_monthly_payment'] && {
        min_budget: value['max_monthly_payment'],
      }),
      ...(value?.['segment'] &&
        !isEmpty(value['segment']) && { segment_type: segment_type }),
      ...(value?.['capacity'] &&
        !isEmpty(value['capacity']) && { capacity: value['capacity'] }),
      ...(value?.['transmission'] &&
        !isEmpty(value['transmission']) && {
          transmission: value['transmission'],
        }),
      ...(value?.['fuel_type'] &&
        !isEmpty(value['fuel_type']) && { fuel_type: value['fuel_type'] }),
      ...(value?.['seat'] &&
        value['seat'].length > 0 && { seat: value['seat'] }),
      ...(value?.['page'] && { page: value['page'] }),
    };
  };

  const onChangeFilter = (value: any) => {
    const filterValueQuery = {
      ...filterValue,
      [popperItem?.key]: value,
    };
    onFilter(filterValueQuery);
    onHandleClose();
  };

  const onNavBarItemClick = (
    event: React.MouseEvent<HTMLElement>,
    key: CarLaunchMonthNavBarItem
  ) => {
    event.preventDefault();
    navItemRef.current?.setFocusedNavItem(key);
    const rectTop =
      document.getElementById(`carLaunchMonthSection_${key}`)?.offsetTop ?? 0;
    const headerOffset =
      document.getElementById('carLaunchFilterHeader')?.offsetHeight ?? 0;
    window.scrollTo({
      top: rectTop - headerOffset,
      behavior: 'smooth',
    });
  };

  const onOpen = () => {
    setDialogOpen(true);
    if (webView) {
      clickAction(basePath, false, {
        title: 'More Filter',
        params: {
          headerShown: false,
          action: 'headerShown',
        },
      });
    }
  };
  const onClose = () => {
    setDialogOpen(false);
    if (webView) {
      clickAction(basePath, false, {
        title: 'More Filter',
        params: {
          headerShown: true,
          action: 'headerShown',
        },
      });
    }
  };

  // =============== BREADCRUMB PATH
  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: 'AutoFacts',
      url: basePath,
    },
    {
      title: 'New Launches',
      url: '',
    },
  ];

  // =============== RENDERER VIEW
  const renderEVButton = () => {
    const filterFuelType = filterValue?.fuel_type;
    const selected =
      filterFuelType &&
      size(filterFuelType) === 1 &&
      filterFuelType[0] === 'ev';
    return (
      <QuickFilterButton
        borderRadius={'4px'}
        selected={selected}
        label={'EV Car'}
        onClick={() => {
          if (selected) {
            onFilter({ ...omit(filterValue, 'fuel_type') });
            return;
          }
          const bodyTypeFilter = {
            ...filterValue,
            fuel_type: ['ev'],
          };
          onFilter(bodyTypeFilter);
        }}
        {...(selected && {
          onDelete: (e) => {
            e.stopPropagation();
            const resetQuery = {
              ...filterValue,
              fuel_type: undefined,
            };
            onFilter(resetQuery);
          },
        })}
      />
    );
  };

  const renderFilter = () => {
    if (!settings) return null;
    return (
      <>
        <Stack
          style={{
            paddingTop: 2,
            marginBottom: 20,
            display: 'inline-grid',
            zIndex: 10,
            backgroundColor: 'white',
            width: '100%',
          }}
        >
          <Stack
            sx={{
              overflowX: 'auto',
              '::-webkit-scrollbar': {
                height: '3px',
              },
              '::-webkit-scrollbar-thumb': {
                backgroundColor: '#31ACBF',
                borderRadius: '50px',
              },
              '::-webkit-scrollbar-thumb:hover': {
                background: '#31ACBF',
              },
              paddingBottom: { xs: 1.5, lg: 0 },
              flexDirection: 'row',
              gap: 2,
            }}
          >
            {/* Filter List */}
            {renderEVButton()}
            {quickButtons.map((filterItem, index) => {
              if (!filterItem?.key) return;

              const renderOutput = () => {
                if (filterItem?.key !== 'body_type') {
                  return filterValue?.[filterItem?.key]
                    ? `${filterItem?.label}: ${
                        filterValue?.[filterItem?.key]?.[0] > 0
                          ? 'RM ' +
                            formatter.format(
                              filterValue?.[filterItem?.key]?.[0]
                            ) +
                            ' - '
                          : ''
                      }RM ${formatter.format(
                        filterValue?.[filterItem?.key]?.[1]
                      )}`
                    : filterItem?.label;
                }
                const value = filterValue?.[filterItem?.key];
                const isArray = value && typeof value !== 'string';
                let label = filterItem?.label;
                if (isArray) {
                  const filterLabel = value.map((text: string) =>
                    text.replace('-', ' ')
                  );
                  label = `${filterItem?.label}: ${filterLabel?.join(', ')}`;
                }

                return label;
              };
              const hasValue =
                filterValue?.[filterItem?.key] &&
                filterValue?.[filterItem?.key].length > 0;

              return (
                <QuickFilterButton
                  key={`filter-${index}`}
                  borderRadius={'4px'}
                  selected={hasValue}
                  label={hasValue ? renderOutput() : filterItem?.label}
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    onHandleClick(filterItem, event)
                  }
                  {...(hasValue && {
                    onDelete: (e) => {
                      e.stopPropagation();
                      const resetQuery = {
                        ...filterValue,
                        [filterItem.key]: undefined,
                      };
                      onFilter(resetQuery);
                    },
                  })}
                />
              );
            })}

            {/* More Filter Action */}
            <QuickFilterButton
              borderRadius={'4px'}
              selected={isFiltered}
              label={'More Filter'}
              icon={
                <Box
                  height={'100%'}
                  display={'flex'}
                  alignItems={'center'}
                  paddingLeft={'7px'}
                  paddingRight={'4px'}
                >
                  <FilterIcon
                    style={{ fill: isFiltered ? '#1E242F' : '#374255' }}
                  />
                </Box>
              }
              onClick={onOpen}
              {...(isFiltered && {
                onDelete: () => null,
                deleteIcon: (
                  <Box paddingRight={'4px'}>
                    <Avatar
                      sx={{
                        bgcolor: '#FB5858',
                        color: 'white',
                        width: 20,
                        height: 20,
                      }}
                    >
                      <Typography
                        fontSize={12}
                        textAlign={'center'}
                        color="white"
                        mt={0.2}
                      >
                        {totalFilter || 0}
                      </Typography>
                    </Avatar>
                  </Box>
                ),
              })}
            />
            {/* Clear Filter */}
            {!isEmpty(filterData) && (
              <Button
                sx={{
                  p: '4px 12px',
                  whiteSpace: 'nowrap',
                }}
                variant="text"
                color="secondary"
                disableRipple
                onClick={onReset}
              >
                Clear All
              </Button>
            )}
            <Popover
              key={popperItem?.key}
              anchorEl={anchorEl}
              open={!!popperItem}
              onClose={onHandleClose}
              slotProps={{
                paper: {
                  sx: {
                    ...(sm
                      ? {
                          top: '0 !important',
                        }
                      : {}),
                    minWidth: { xs: 'auto', md: 458 },
                  },
                },
              }}
            >
              <Stack sx={{ maxWidth: 458, gap: '10px' }}>
                <Typography fontWeight="500" mt={5} ml={5}>
                  {popperItem?.label}
                </Typography>
                {popperItem?.render?.(
                  filterValue?.[popperItem?.key],
                  onChangeFilter
                )}
              </Stack>
            </Popover>
          </Stack>
        </Stack>
      </>
    );
  };

  const renderNavItem = () => (
    <CategoriesBoxWrapper $show={true} id="carLaunchFilterHeader">
      <div
        style={{
          borderBottom: '1px solid #ECECEC',
          position: 'relative',
          backgroundColor: 'white',
          marginBottom: 15,
        }}
      >
        <CarLaunchMonthHeaderNavBarItem
          ref={navItemRef}
          handleClick={onNavBarItemClick}
          sectionsData={filterData.length > 0 ? filterData : carNewLaunch}
        />
      </div>
      {renderFilter()}
    </CategoriesBoxWrapper>
  );

  const renderList = () => (
    <CarLaunchMonthSectionContent
      webView={webView}
      sectionsData={filterData.length > 0 ? filterData : carNewLaunch}
      sectionRef={sectionRef.current}
    />
  );

  const renderCarList = () => (
    <TitleDivider
      title={webView ? '' : 'New Launches'}
      paddingBlock="0"
      titleVariant="h1"
      webView={webView}
    >
      {/* FILTER DIALOG */}
      {settings && (
        <FilterSection
          open={dialogOpen}
          value={filterValue}
          settings={settings}
          onClose={onClose}
          onSubmit={onFilter}
          onReset={onReset}
          filterDialogRef={filterDialogRef}
        />
      )}

      <Stack
        sx={{
          gap: 5,
          marginTop: 0,
        }}
      >
        {/* MONTH SELECT & FILTER */}
        {renderNavItem()}

        {/* CAR LIST */}
        {renderList()}
      </Stack>
    </TitleDivider>
  );

  // =============== VIEWS
  return (
    <Box
      sx={{
        overscrollBehavior: 'none',
        mt: webView ? -4.5 : 0,
      }}
    >
      {/* BREADCRUMB */}
      {!webView && (
        <Stack
          sx={{
            display: 'flex',
          }}
        >
          <Breadcrumb options={breadcrumbOpt} bold />
        </Stack>
      )}

      {/* CONTENT */}
      <Stack sx={{ gap: 1 }}>
        <Stack
          style={{
            flexDirection: lg ? 'column' : 'row',
            gap: lg ? 0 : 30,
            paddingTop: webView ? 0 : '1rem',
          }}
          divider={lg ? undefined : <Divider orientation="vertical" flexItem />}
        >
          <Stack
            sx={{
              maxWidth: {
                xl: '70%',
                xs: '100%',
              },
            }}
            // style={{
            //   maxWidth: lg ? '100%' : '70%',
            // }}
          >
            {renderCarList()}
          </Stack>

          {!webView && <CarLaunchNews webView={webView} />}
        </Stack>

        <BrandFilter
          title={isMobile ? undefined : 'Popular Brands in Malaysia'}
          brands={desktopPopularBrands}
          webView={webView}
          showInnerTitle={isMobile}
          backgroundColor={isMobile ? undefined : '#fff'}
          mobilePadding={isMobile ? '15px 10px' : '25px'}
        />

        <RecommendCarList webView={webView} />
      </Stack>
    </Box>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default NewLaunchScreen;
